import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '../../shared/components';
import { apiRequest } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';

type Place = { place_id: string; name: string; city?: string; district?: string };
type Entry = { _id: string; active: boolean; reason: string; place: Place };
export function NegativeListPage() {
  const { session } = useAuth();
  const [items, setItems] = useState<Entry[]>([]);
  const [places, setPlaces] = useState<Place[]>([]);
  const [query, setQuery] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!session?.expert_id) return;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      apiRequest<{ items: Entry[]; places: Place[] }>('/admin/marketing/negative-list', { query: { admin_id: session.expert_id, search: query } })
        .then(data => { if (!cancelled) { setItems(data.items); setPlaces(data.places); setError(''); } })
        .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดรายการไม่สำเร็จ'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, query ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [session?.expert_id, query, version]);
  const closeAdd = () => { setAdding(false); setQuery(''); setReason(''); setPlaces([]); };
  async function save(id: string, active: boolean, note: string, name = '') {
    setBusy(true); setError(''); setNotice('');
    try {
      await apiRequest(`/admin/marketing/negative-list/${encodeURIComponent(id)}`, { method: 'PUT', body: { admin_id: session?.expert_id, active, reason: note } });
      if (adding) { closeAdd(); setNotice(`เพิ่ม ${name || id} ใน Negative List แล้ว`); }
      setQuery(''); setReason(''); setVersion(v => v + 1);
    } catch (e) { setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
    finally { setBusy(false); }
  }
  return <WorkspaceLayout kind="admin" title="Negative List" description="ลดลำดับร้านลง 1 อันดับในหน้าแรก แผนที่ และการค้นหา โดยยังแสดงร้านตามปกติ">
    <section className="panel" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}><h2 style={{ margin: 0 }}>ร้านใน Negative List</h2><button className="button button--primary" onClick={() => { closeAdd(); setError(''); setNotice(''); setAdding(true); }}><Plus size={17} />เพิ่มร้าน</button></div>
      {notice && <p className="success-notice" role="status">{notice}</p>}
      {error && !adding && <p role="alert">{error}</p>}
      <p>ปรับหลังจัดอันดับปกติ ก่อนแบ่งหน้า ร้านที่อยู่ท้ายรายการจะไม่เลื่อนเกินท้ายรายการ</p>
      {!loading && !items.length && <p>ยังไม่มีร้านในรายการ</p>}
      {items.map(item => <div key={item._id} style={{ display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #e0e6f2', padding: '16px 0' }}>
        <span><strong>{item.place.name || item._id}</strong><br />{item.place.district} · {item.place.city}<br /><small>{item._id}</small><br />{item.active ? 'ลด 1 อันดับ' : 'คืนลำดับปกติแล้ว'}{item.reason && <p>{item.reason}</p>}</span>
        <button className="button button--ghost" disabled={busy} onClick={() => void save(item._id, !item.active, item.reason)}>{item.active ? 'คืนลำดับปกติ' : 'ลดลำดับอีกครั้ง'}</button>
      </div>)}
    </section>
    {adding && <Modal title="เพิ่มร้านที่ลดลำดับการมองเห็น" onClose={closeAdd}>
      <div style={{ padding: '18px 22px 22px', display: 'grid', gap: 14 }}>
      <label className="field">ค้นหาชื่อร้านหรือ Place ID<input value={query} onChange={e => { setQuery(e.target.value); setPlaces([]); }} placeholder="เช่น FOEI" /></label>
      <label className="field">เหตุผล (ไม่บังคับ)<input value={reason} onChange={e => setReason(e.target.value)} maxLength={1000} /></label>
      {error && <p className="form-error" role="alert">{error}</p>}
      {loading && <p role="status">กำลังโหลด…</p>}
      {!loading && query && !places.length && <p>ไม่พบร้านที่ค้นหา</p>}
      {!loading && places.map(place => <div key={place.place_id} style={{ display: 'flex', gap: 16, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', padding: '12px 0' }}>
        <span><strong>{place.name}</strong><br />{place.district} · {place.city}<br /><small>{place.place_id}</small></span>
        <button className="button button--primary" disabled={busy || items.some(item => item._id === place.place_id && item.active)} onClick={() => void save(place.place_id, true, reason, place.name)}>เพิ่มในรายการ</button>
      </div>)}
      </div>
    </Modal>}
  </WorkspaceLayout>;
}
